import { useState, useEffect } from 'react';
import { Upload, CheckCircle2, Link, Share2 } from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';
import axios from 'axios';
import io, { Socket } from 'socket.io-client';
import { useParams } from 'react-router-dom';

const baseUrl = import.meta.env.VITE_API_URL;

function MobileUpload() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [socket, setSocket] = useState<Socket | null>(null);

  const { sessionId } = useParams<{ sessionId: string }>();

  // Initialize socket on-demand when sessionId exists
  useEffect(() => {
    if (sessionId && !socket) {
      const newSocket = io(`${baseUrl}`);
      setSocket(newSocket);

      // Register session
      newSocket.emit('register', { sessionId, client: 'mobile' });

      // Cleanup on unmount
      return () => {
        newSocket.disconnect();
        setSocket(null);
      };
    }
  }, [sessionId]);

  // Preview selected image
  useEffect(() => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result as string);
      reader.readAsDataURL(file);
    } else {
      setPreview('');
    }
  }, [file]);

  // Handle file upload
  const handleUpload = async () => {
    if (!file || !socket) return;

    try {
      setUploading(true);

      const form = new FormData();
      form.append('file', file);

      await axios.post(`${baseUrl}/upload/${sessionId}`, form);

      // Notify desktop in real-time
      socket.emit('image-uploaded', { sessionId, filename: file.name });

      setUploadSuccess(true);
      setTimeout(() => setUploadComplete(true), 500);
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  const resetUpload = () => {
    setFile(null);
    setPreview('');
    setUploadSuccess(false);
    setUploadComplete(false);
  };

  return (
    <div className="min-vh-100 bg-light d-flex justify-content-center align-items-start py-5 px-3">
      <div className="w-100" style={{ maxWidth: '500px' }}>
        <div className="text-center mb-4">
          <h1 className="h4 fw-bold text-dark mb-1">Goodhand.ae File Transfer</h1>
          <p className="text-muted small">Secure document transfer portal</p>
        </div>

        <div className="alert alert-success d-flex align-items-start gap-2 p-3 mb-4">
          <CheckCircle2 className="text-success" size={20} />
          <div>
            <p className="mb-1 small fw-medium text-success">Connected to Goodhand.ae</p>
            <p className="mb-0 text-success small">Session ID: {sessionId}</p>
          </div>
        </div>

        {uploadComplete ? (
          <>
            <div className="card border mb-3">
              <div className="card-body">
                <h5 className="card-title">Upload Complete</h5>
                <div className="border p-3 mb-3 rounded">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="small">{file?.name}</span>
                    <div className="d-flex align-items-center gap-1">
                      <span className="text-success small">Complete</span>
                      <CheckCircle2 className="text-success" size={16} />
                    </div>
                  </div>
                  <div className="progress" style={{ height: '6px' }}>
                    <div className="progress-bar bg-success w-100" role="progressbar" />
                  </div>
                  <p className="text-muted mt-2 mb-0 small">Securely stored</p>
                </div>

                <div className="alert alert-success d-flex gap-2 p-3">
                  <div className="bg-success bg-opacity-10 p-1 rounded-circle">
                    <CheckCircle2 className="text-success" size={20} />
                  </div>
                  <div>
                    <p className="mb-1 fw-medium text-success small">Files uploaded successfully</p>
                    <p className="mb-0 text-success small">Your files are now securely stored on Goodhand.ae</p>
                  </div>
                </div>

                <button className="btn btn-light w-100 mb-2 d-flex align-items-center justify-content-center gap-2 d-none">
                  <Share2 size={18} />
                  Share Access Link
                </button>
                <button className="btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center gap-2" onClick={resetUpload}>
                  <Upload size={18} />
                  Upload Files (Re-Take)
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="card border">
            <div className="card-body">
              <h5 className="card-title">Upload Files</h5>
              <p className="card-text text-muted small">Select files to upload to Goodhand.ae</p>

              <div
                className="border border-secondary border-dashed rounded p-4 text-center mb-3"
                style={{ borderStyle: 'dashed' }}
              >
                <Upload size={48} className="text-secondary mb-3" />
                <p className="mb-1 fw-medium">Drag & drop your files here</p>
                <p className="text-muted small mb-3">or click to browse from your device</p>
                <button className="btn btn-light btn-sm" onClick={() => document.getElementById('fileInput')?.click()}>
                  Select Files
                </button>
                <input
                  id="fileInput"
                  type="file"
                  className="d-none"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  accept=".jpg,.png,.pdf"
                />
              </div>

              {file && (
                <div className="mb-3 p-2 bg-light rounded border">
                  <p className="small mb-2 text-muted">Selected file: {file.name}</p>
                  {preview && (
                    <img src={preview} alt="Preview" className="img-fluid rounded" />
                  )}
                </div>
              )}

              <div className="d-flex justify-content-between small text-muted mb-3">
                <div className="d-flex align-items-center gap-1">
                  <Link size={14} />
                  <span>End-to-end encrypted</span>
                </div>
                <span>Max size: 50MB</span>
              </div>

              <button
                className={`btn w-100 d-flex align-items-center justify-content-center gap-2 text-white fw-medium ${
                  !file || uploading ? 'btn-primary disabled' : 'btn-primary'
                }`}
                onClick={handleUpload}
                disabled={!file || uploading}
              >
                {uploading ? (
                  <>
                    <div className="spinner-border spinner-border-sm text-white" role="status" />
                
                  </>
                ) : uploadSuccess ? (
                  <>
                    <CheckCircle2 size={18} />
                    Uploaded Successfully!
                  </>
                ) : (
                  <>
                    <Upload size={18} />
                    Upload Document
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        <p className="text-center text-muted small mt-4 mb-0">
          Goodhand.ae Secure File Transfer © 2025
        </p>
        <p className="text-center text-muted small">For support, contact: support@goodhand.ae</p>
      </div>
    </div>
  );
}

export default MobileUpload;

